import { useMemo, useCallback } from "react"
import { map, find, keys, capitalize, omitBy, keyBy, mapValues, pick, compact } from "lodash"
import PropTypes from "prop-types"
import classNames from "classnames"
import { Paper } from "@mui/material"
import { UUID, ACCOUNT_MEMBER, ROLE } from "helpers/shapes"
import { TEAM_ROLES, isAllAccessTeam } from "helpers/permissions/teams"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import Table from "components/material/table"
import { TEAM_MEMBER } from "./shapes"

export default function TeamMembers({ account, userId, teamId, teamAlias, teamRole, role, teamMembers, accountMembers, update, tabs }) {
  const isAdmin = role === "admin" || teamRole === "ADMIN"
  const accountMembersById = useMemo(() => keyBy(accountMembers, "id"), [accountMembers])

  const optionLabels = useMemo(() => mapValues(accountMembersById, ({ name }) => name), [accountMembersById])

  const availableOptionLabels = useMemo(() =>
    omitBy(optionLabels, (_, id) => find(teamMembers, m => m.id === id)),
  [teamMembers, optionLabels])

  // There are no Team Admins for the All access team
  const roleOptions = compact(["MEMBER", !isAllAccessTeam(teamAlias) && "ADMIN"])

  const addForm = useMemo(() => {
    if (!isAdmin) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($teamId: IdOrAlias!, $role: TeamRole!) {
        ${map(data.members, (memberId, index) => `member${index}: addTeamMember(teamId: $teamId, memberId: "${memberId}", role:$role) { email name }`)}
      }`

      const variables = { role: data.role, teamId }

      update(query, variables, account, {
        reloadData: ["teamMembers", "memberDatasetEntitlements", "member", "userDatasets", "models", "datasetTeamEntitlements"],
      }, cb)
    }

    return {
      title: "Add Team Members",
      submitText: "Add Members",
      track: {
        type: "new_team_member_added",
        payload: ({ members, role }) => ({
          emails: map(members, id => accountMembersById[id].email),
          role,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "members",
        label: "Members",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }, {
        id: "role",
        label: "Role",
        type: "select",
        options: roleOptions,
        optionLabels: TEAM_ROLES,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [isAdmin, availableOptionLabels, roleOptions, teamId, update, account, accountMembersById])

  const editForm = useMemo(() => {
    if (!isAdmin) { return null }

    function onEdit(data, cb) {
      const query = `mutation ($teamId: IdOrAlias!, $memberId: IdOrEmail!, $role: TeamRole!) {
        updateTeamMemberRole(teamId: $teamId, memberId: $memberId, role: $role) { teamRole }
      }`

      const variables = { teamId, memberId: data.id, role: data.role }

      update(query, variables, account, {
        reloadData: "teamMembers",
      }, cb)
    }

    return {
      title: "Edit Team Member role",
      isEnabled: () => isAdmin,
      submitText: "Save",
      track: {
        type: "edit_team_member_role",
        payload: data => pick(data, "role"),
      },
      onSubmit: onEdit,
      getInitialValues: ({ teamRole }) => ({ role: teamRole }),
      fields: [{
        id: "role",
        label: "Role",
        type: "select",
        options: roleOptions,
        optionLabels: TEAM_ROLES,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [isAdmin, roleOptions, teamId, update, account])

  const deleteForm = useMemo(() => {
    if (!isAdmin) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($teamId: IdOrAlias!) {
        ${map(data, (member, index) => `member${index}: removeTeamMember(teamId: $teamId, memberId: "${member.id}") { ok }`)}
      }`

      const variables = { teamId }

      update(query, variables, account, {
        reloadData: ["teamMembers", "memberDatasetEntitlements", "member", "userDatasets", "models", "datasetTeamEntitlements"],
      }, cb)
    }

    return {
      type: "team member",
      track: {
        type: "delete_team_member_confirmed",
        payload: data => pick(data, "name", "email"),
      },
      isEnabled: () => isAdmin,
      renderItem: member => <li key={member.id}>{member.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, isAdmin, teamId, update])

  const linkTo = useCallback((member) => {
    if (role === "admin" || member.id === userId) {
      return `/${account}/home/members/${member.id}`
    }
  }, [account, role, userId])

  return <div className={classNames("modules_team-members", "with-header")}>
    <UpsellTabs id="team-members" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Team Members"
        idField="id"
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
        data={teamMembers}
        columns={[{
          name: "Name",
          id: "name",
          linkTo,
        }, {
          name: "Email",
          id: "email",
        }, {
          name: "Team Role",
          id: "teamRole",
          formatter: capitalize,
        }]}
      />
    </Paper>
  </div>
}

TeamMembers.propTypes = {
  account: PropTypes.string.isRequired,
  role: ROLE.isRequired,
  teamRole: PropTypes.oneOf(["ADMIN", "MEMBER"]),
  teamAlias: PropTypes.string.isRequired,
  teamId: UUID,
  teamMembers: PropTypes.arrayOf(TEAM_MEMBER),
  accountMembers: PropTypes.arrayOf(ACCOUNT_MEMBER).isRequired,
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
  userId: PropTypes.string.isRequired,
}
